import { SUPABASE_MIGRATIONS_FOLDER } from '~/lib/constants';
import { availableRegions } from '~/lib/data/supabase';
import { applicationManager } from '~/lib/stores/applicationManager';
import { supabaseStore } from '~/lib/stores/supabase';
import type { SupabaseProject, SupabaseRegion } from '~/types/supabase';
import { stripIndents } from '~/utils/stripIndent';

const TITLE_REGEX = /\s*#\s*(.+)/;

const SOUTH_AMERICA_CITIES = ['Sao_Paulo', 'Argentina', 'Montevideo', 'Asuncion'];
const US_WEST_CITIES = ['Los_Angeles', 'Vancouver', 'Phoenix'];
const US_EAST_CITIES = ['New_York', 'Toronto', 'Montreal'];
const US_CENTRAL_CITIES = ['Chicago', 'Indiana', 'Detroit'];
const US_SOUTH_CITIES = ['Mexico_City', 'Denver', 'Chicago'];

const UK_IRELAND_CITIES = ['London', 'Dublin'];
const FRANCE_REGION_CITIES = ['Paris', 'Brussels', 'Zurich'];
const CENTRAL_EUROPE_CITIES = ['Berlin', 'Amsterdam', 'Vienna', 'Prague', 'Warsaw'];
const NORDIC_CITIES = ['Stockholm', 'Oslo', 'Copenhagen', 'Helsinki', 'Reykjavik'];
const SOUTH_EUROPE_CITIES = ['Madrid', 'Lisbon', 'Rome'];

const JAPAN_CITIES = ['Tokyo'];
const EAST_ASIA_CITIES = ['Seoul', 'Hong_Kong', 'Taipei'];
const SOUTHEAST_ASIA_CITIES = ['Singapore', 'Jakarta', 'Bangkok', 'Manila', 'Kuala_Lumpur', 'Ho_Chi_Minh'];
const INDIA_CITIES = ['Kolkata'];

export function isDatabaseMigrationFile(relativePath: string) {
  return relativePath.startsWith(SUPABASE_MIGRATIONS_FOLDER) && relativePath.endsWith('.sql');
}

export function createDatabaseMigrationPath(migrationsDate: string, name: string) {
  return `${SUPABASE_MIGRATIONS_FOLDER}/${`${migrationsDate}`}_${name}.sql`;
}

export function extractQueryParts(fileContent: string) {
  const { start, end, hasSummary } = findSummaryBounds(fileContent);

  let title = '';
  let summary = hasSummary ? fileContent.slice(start + 2, end) : fileContent.slice(0, 100) + '...';

  if (hasSummary) {
    const titleMatch = summary.match(TITLE_REGEX);

    title = titleMatch ? titleMatch[1].trim() : '';

    if (titleMatch && typeof titleMatch.index !== 'undefined') {
      summary = stripIndents(summary.slice(titleMatch.index + titleMatch[0].length)).trim();
    }
  }

  const query = hasSummary ? fileContent.slice(end + 2) : fileContent;

  return { title, summary, query };
}

function findSummaryBounds(sqlFileContent: string) {
  const summaryStart = sqlFileContent.indexOf('/*');
  const summaryEnd = sqlFileContent.indexOf('*/', summaryStart + 2);

  return {
    start: summaryStart,
    end: summaryEnd,
    hasSummary: summaryStart !== -1 && summaryEnd !== -1,
  };
}

export async function reconnectSupabase(onSuccess: () => void) {
  let unsubscribe = () => {
    // noop
  };

  // only time out after 2 minutes because the oauth flow might take longer when people still need to sign up
  applicationManager.connect('SUPABASE', { timeout: 120_000 });

  try {
    await new Promise<void>((resolve, reject) => {
      unsubscribe = applicationManager.applications.subscribe((newValue, _, changedKey) => {
        if (changedKey !== 'SUPABASE') {
          return;
        }

        if (newValue.SUPABASE === 'CONNECTED') {
          resolve();
        } else if (newValue.SUPABASE === 'DISCONNECTED') {
          reject();
        }
      });
    });

    onSuccess();
  } catch {
    supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
  }

  unsubscribe();
}

export function sortProjectsByStatus(a: SupabaseProject, b: SupabaseProject): number {
  if (a.status === 'INACTIVE' && b.status !== 'INACTIVE') {
    return 1;
  }

  if (a.status !== 'INACTIVE' && b.status === 'INACTIVE') {
    return -1;
  }

  if (a.status === 'ACTIVE_HEALTHY' && b.status !== 'ACTIVE_HEALTHY') {
    return -1;
  }

  if (a.status !== 'ACTIVE_HEALTHY' && b.status === 'ACTIVE_HEALTHY') {
    return 1;
  }

  return 0;
}

export function getClosestRegion() {
  const closestRegionId = getClosestRegionId();

  return availableRegions.find((region) => region.id === closestRegionId) || availableRegions[0];
}

export function getClosestRegionId(): SupabaseRegion {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!timezone) {
      return 'us-east-1';
    }

    const [continent, location] = timezone.split('/');

    switch (continent) {
      case 'America': {
        if (SOUTH_AMERICA_CITIES.includes(location)) {
          return 'sa-east-1';
        }

        if (US_WEST_CITIES.includes(location)) {
          return 'us-west-1';
        }

        if (US_EAST_CITIES.includes(location)) {
          return 'us-east-1';
        }

        if (US_CENTRAL_CITIES.includes(location)) {
          return 'us-east-2';
        }

        if (US_SOUTH_CITIES.includes(location)) {
          return 'ca-central-1';
        }

        return 'us-east-1';
      }

      case 'Europe': {
        if (UK_IRELAND_CITIES.includes(location)) {
          return 'eu-west-2';
        }

        if (FRANCE_REGION_CITIES.includes(location)) {
          return 'eu-west-3';
        }

        if (CENTRAL_EUROPE_CITIES.includes(location)) {
          return 'eu-central-1';
        }

        if (NORDIC_CITIES.includes(location)) {
          return 'eu-north-1';
        }

        if (SOUTH_EUROPE_CITIES.includes(location)) {
          return 'eu-west-1';
        }

        return 'eu-central-1';
      }

      case 'Asia': {
        if (JAPAN_CITIES.includes(location)) {
          return 'ap-northeast-1';
        }

        if (EAST_ASIA_CITIES.includes(location)) {
          return 'ap-northeast-2';
        }

        if (SOUTHEAST_ASIA_CITIES.includes(location)) {
          return 'ap-southeast-1';
        }

        if (INDIA_CITIES.includes(location)) {
          return 'ap-south-1';
        }

        return 'ap-southeast-2';
      }

      case 'Australia':
      case 'Pacific': {
        return 'ap-southeast-2';
      }

      default: {
        return 'us-east-1';
      }
    }
  } catch {
    return 'us-east-1';
  }
}
